/**
 * Web upload queue behind DataSource.uploadFile. Each upload is kept in IndexedDB (idb-keyval) — its metadata and the
 * Blob itself — until the server has the bytes, so uploads survive reloads and wait offline, then resume:
 * a single PUT re-sends the whole file, a multipart upload re-sends only the parts the server doesn't have.
 * States follow core logic/files (queued → uploading → done | failed → retry).
 */
import type { FileRef, UploadPlan } from "@bundle/contracts";
import {
  errorKind,
  Files,
  type ApiClient,
  type NetworkMonitor,
  type Observable,
  type PickedFile,
  type UploadEntry,
  type UploadHandle,
  type UploadState,
  type UploadTarget,
} from "@bundle/core";
import { createStore, del, entries, set, type UseStore } from "idb-keyval";
import { ulid } from "ulid";
import { reportBillingFault } from "../billingFaults.ts";
import { inspectMedia } from "./thumbnail.ts";

type Rec = {
  id: string;
  ws: string;
  target: UploadTarget;
  name: string;
  mime: string;
  size: number;
  lastModified: number;
  width?: number;
  height?: number;
  state: UploadState;
  createdAt: number;
  /** Measured + thumbnail made (once). */
  inspected: boolean;
  /** Server upload (after createUpload). */
  fileId?: string;
  plan?: UploadPlan;
  thumbDone: boolean;
  /** Files: hex SHA-256 of the content (computed once, before createUpload: the workspace may already have it). */
  sha256?: string;
  /** The server already had the bytes ("Already in Bundle"). */
  instant?: boolean;
};

/** IndexedDB layout: `r:<id>` → record (+ thumbnail), `b:<id>` → the file's Blob (written once, dropped when stored). */
type StoredRec = { rec: Rec; thumb?: Blob };

const MAX_ACTIVE = 2;
/** Bytes hashed per chunk (Files' sha256: streamed, so a 5 TB file never sits in memory at once). */
const HASH_CHUNK = 8 * 1024 * 1024;

/** Hex SHA-256 of a blob, streamed through hash-wasm in chunks. */
async function sha256Of(blob: Blob): Promise<string> {
  const { createSHA256 } = await import("hash-wasm");
  const hasher = await createSHA256();
  hasher.init();
  for (let at = 0; at < blob.size; at += HASH_CHUNK) {
    hasher.update(new Uint8Array(await blob.slice(at, Math.min(blob.size, at + HASH_CHUNK)).arrayBuffer()));
  }
  return hasher.digest("hex");
}
/** Done uploads nobody references are dropped after a day (the server deletes unattached uploads then too). */
const DONE_TTL_MS = 86_400_000;

export class UploadManager {
  private readonly recs = new Map<string, Rec>();
  private readonly blobs = new Map<string, Blob>();
  private readonly thumbs = new Map<string, Blob>();
  private readonly previews = new Map<string, string>();
  /** Server file id → local Blob of a finished upload (shows the just-sent file without downloading it again). */
  private readonly sentBlobs = new Map<string, Blob>();
  private readonly sentUrls = new Map<string, string>();
  private readonly waiters = new Map<string, { resolve: (f: FileRef) => void; reject: (e: Error) => void }[]>();
  private readonly listeners = new Set<() => void>();
  private readonly observables = new Map<string, Observable<UploadEntry[]>>();
  private readonly snapshots = new Map<string, UploadEntry[]>();
  private readonly active = new Set<string>();
  private readonly removed = new Set<string>();
  private readonly store: UseStore;
  private version = 0;
  /** Restored from IndexedDB (before that, `has()` doesn't know about earlier uploads). */
  restored = false;
  readonly ready: Promise<void>;

  constructor(
    private readonly api: ApiClient,
    private readonly network: NetworkMonitor,
    namespace: string,
  ) {
    this.store = createStore(`${namespace}-uploads`, "uploads");
    this.ready = this.restore().finally(() => {
      this.restored = true;
    });
    network.subscribe(() => {
      if (network.getSnapshot()) this.pump();
    });
  }

  // ---------- Port surface ----------

  add(ws: string, file: PickedFile, target: UploadTarget): UploadHandle {
    const id = ulid();
    const rec: Rec = {
      id,
      ws,
      target,
      name: file.name,
      mime: file.mime,
      size: file.size,
      lastModified: file.lastModified,
      state: Files.newUpload(file.size),
      createdAt: Date.now(),
      inspected: false,
      thumbDone: false,
    };
    const blob = file.source as Blob;
    this.recs.set(id, rec);
    this.blobs.set(id, blob);
    const done = new Promise<FileRef>((resolve, reject) => this.waiters.set(id, [{ resolve, reject }]));
    // Callers that only observe `uploads()` don't await `done`.
    done.catch(() => undefined);
    void set(`b:${id}`, blob, this.store).catch(() => undefined);
    void this.persist(id);
    this.changed();
    this.pump();
    return { id, done };
  }

  uploads(ws: string): Observable<UploadEntry[]> {
    let o = this.observables.get(ws);
    if (o === undefined) {
      o = {
        subscribe: (l) => {
          this.listeners.add(l);
          return () => this.listeners.delete(l);
        },
        getSnapshot: () => this.snapshot(ws),
      };
      this.observables.set(ws, o);
    }
    return o;
  }

  /** Every upload's state by id (outbox gate). */
  states(): Record<string, UploadState> {
    const out: Record<string, UploadState> = {};
    for (const r of this.recs.values()) out[r.id] = r.state;
    return out;
  }

  /** Uploads sending bytes right now (automatic updates wait for them). */
  inFlight(): number {
    return this.active.size;
  }

  has(id: string): boolean {
    return this.recs.has(id);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  retry(id: string): void {
    const r = this.recs.get(id);
    if (r === undefined) return;
    this.dispatch(r, { type: "retry" });
    this.pump();
  }

  /** Drops an upload; `deleteRemote`: also cancel it server-side (ignored when it's already attached). */
  remove(id: string, deleteRemote = true): void {
    const r = this.recs.get(id);
    if (r === undefined) return;
    this.removed.add(id);
    this.recs.delete(id);
    this.blobs.delete(id);
    this.thumbs.delete(id);
    const url = this.previews.get(id);
    if (url !== undefined) URL.revokeObjectURL(url);
    this.previews.delete(id);
    void del(`r:${id}`, this.store).catch(() => undefined);
    void del(`b:${id}`, this.store).catch(() => undefined);
    for (const w of this.waiters.get(id) ?? []) w.reject(new Error("upload removed"));
    this.waiters.delete(id);
    if (deleteRemote && r.fileId !== undefined) void this.api.deleteFile(r.ws, r.fileId).catch(() => undefined);
    this.changed();
  }

  /** Object URL of a local upload's bytes (`local-upload:<id>`), or of a just-sent file (by server file id). */
  localUrl(id: string): string | undefined {
    const cached = this.previews.get(id) ?? this.sentUrls.get(id);
    if (cached !== undefined) return cached;
    const blob = this.blobs.get(id);
    if (blob !== undefined) {
      const url = URL.createObjectURL(blob);
      this.previews.set(id, url);
      return url;
    }
    const sent = this.sentBlobs.get(id);
    if (sent === undefined) return undefined;
    const url = URL.createObjectURL(sent);
    this.sentUrls.set(id, url);
    return url;
  }

  /** Forget everything (sign out). */
  async clear(): Promise<void> {
    for (const id of this.recs.keys()) this.remove(id, false);
    for (const url of this.sentUrls.values()) URL.revokeObjectURL(url);
    this.sentUrls.clear();
    this.sentBlobs.clear();
    const all = await entries<string, unknown>(this.store).catch(() => [] as [string, unknown][]);
    await Promise.all(all.map(([k]) => del(k, this.store).catch(() => undefined)));
  }

  // ---------- Queue ----------

  private pump(): void {
    if (!this.network.getSnapshot()) return;
    for (const r of [...this.recs.values()].sort((a, b) => a.createdAt - b.createdAt)) {
      if (this.active.size >= MAX_ACTIVE) return;
      if (r.state.phase !== "queued" || this.active.has(r.id)) continue;
      this.active.add(r.id);
      void this.run(r.id).finally(() => {
        this.active.delete(r.id);
        this.pump();
      });
    }
  }

  private async run(id: string): Promise<void> {
    const r = this.recs.get(id);
    const blob = this.blobs.get(id);
    if (r === undefined) return;
    this.dispatch(r, { type: "start" });
    try {
      if (blob === undefined) throw Object.assign(new Error("The file's bytes are gone"), { kind: "invalid" });
      if (!r.inspected) {
        const info = await inspectMedia(blob, r.mime, r.name);
        if (info.width !== undefined) r.width = info.width;
        if (info.height !== undefined) r.height = info.height;
        if (info.thumb !== undefined) this.thumbs.set(id, info.thumb);
        r.inspected = true;
        void this.persist(id);
        this.changed();
      }
      if (r.fileId === undefined || r.plan === undefined) {
        const req: Parameters<ApiClient["createUpload"]>[1] = { name: r.name, size: r.size, clientId: r.id };
        if (r.mime !== "") req.mime = r.mime;
        if (r.target.type === "channel") req.channelId = r.target.channelId;
        else if (r.target.type === "page") req.pageId = r.target.pageId;
        else if (r.target.type === "avatar") req.purpose = "avatar";
        else if (r.target.type === "workspaceIcon") req.purpose = "workspace_icon";
        else if (r.target.type === "import") req.purpose = "import";
        else if (r.target.type === "mail") req.purpose = "mail";
        else if (r.target.type === "huddle") req.purpose = "huddle";
        else if (r.target.type === "drive") req.purpose = "drive";
        else req.purpose = "sticker";
        if (r.target.type === "drive") {
          if (r.sha256 === undefined) {
            r.sha256 = await sha256Of(blob);
            void this.persist(id);
          }
          req.sha256 = r.sha256;
        }
        if (r.width !== undefined && r.height !== undefined && r.width > 0 && r.height > 0) {
          req.width = r.width;
          req.height = r.height;
        }
        const res = await this.api.createUpload(r.ws, req);
        r.fileId = res.file.id;
        r.plan = res.upload;
        void this.persist(id);
        if (res.status === "ready") {
          r.instant = true;
          return this.finish(r, res.file, blob);
        }
      }
      if (this.removed.has(id)) return;
      const thumb = this.thumbs.get(id);
      if (thumb !== undefined && !r.thumbDone) {
        try {
          await this.api.uploadThumb(r.ws, r.fileId, thumb);
        } catch (e) {
          if (errorKind(e) === "network") throw e;
        }
        r.thumbDone = true;
      }
      let file: FileRef;
      if (r.plan.mode === "single") {
        file = await this.api.uploadContent(r.ws, r.fileId, blob, (sent) => this.dispatch(r, { type: "progress", sent }));
      } else {
        const partSize = r.plan.partSize ?? Files.UPLOAD_PART_BYTES;
        const count = r.plan.partCount ?? Math.ceil(r.size / partSize);
        const have = new Set(r.plan.uploadedParts ?? []);
        const partLength = (p: number) => Math.min(partSize, r.size - (p - 1) * partSize);
        // Up to 10,000 parts: keep a running total instead of summing the stored parts each time.
        let base = [...have].reduce((sum, p) => sum + partLength(p), 0);
        for (let n = 1; n <= count; n++) {
          if (have.has(n)) continue;
          if (this.removed.has(id)) return;
          const start = (n - 1) * partSize;
          const before = base;
          // "direct": the part goes straight to R2 on a presigned URL (too big for an API request body).
          const put = r.plan.mode === "direct" ? this.api.uploadPartDirect : this.api.uploadPart;
          await put(r.ws, r.fileId, n, blob.slice(start, Math.min(r.size, start + partSize)), (sent) =>
            this.dispatch(r, { type: "progress", sent: before + sent }),
          );
          base += partLength(n);
          have.add(n);
          r.plan = { ...r.plan, uploadedParts: [...have].sort((a, b) => a - b) };
          void this.persist(id);
        }
        file = await this.api.completeUpload(r.ws, r.fileId);
      }
      this.finish(r, file, blob);
    } catch (e) {
      if (this.removed.has(id)) return;
      let kind = (e as { kind?: string }).kind ?? errorKind(e);
      const status = (e as { status?: number }).status;
      if (status === 404 || status === 409) {
        // Swept on the server, or finished by an earlier attempt: start over (createUpload is idempotent on our id).
        delete r.fileId;
        delete r.plan;
        kind = "server";
      }
      if (!this.network.getSnapshot() || (kind === "network" && typeof navigator !== "undefined" && navigator.onLine === false)) {
        this.dispatch(r, { type: "pause" });
        return;
      }
      this.dispatch(r, { type: "fail", error: kind });
      // Out of storage on the plan (or read-only): the shell's billing prompt says so; retrying won't help.
      const billing = reportBillingFault(r.ws, e);
      if (!billing && Files.shouldAutoRetry(r.state)) {
        setTimeout(() => this.retry(id), Files.uploadRetryDelay(r.state.attempts));
      } else {
        for (const w of this.waiters.get(id) ?? []) w.reject(new Error(`upload failed: ${kind}`));
        this.waiters.delete(id);
      }
    }
  }

  private finish(r: Rec, file: FileRef, blob: Blob): void {
    this.dispatch(r, { type: "succeed", file });
    this.sentBlobs.set(file.id, blob);
    if (this.sentBlobs.size > 40) {
      const oldest = this.sentBlobs.keys().next().value as string;
      this.sentBlobs.delete(oldest);
      const url = this.sentUrls.get(oldest);
      if (url !== undefined) URL.revokeObjectURL(url);
      this.sentUrls.delete(oldest);
    }
    for (const w of this.waiters.get(r.id) ?? []) w.resolve(file);
    this.waiters.delete(r.id);
    // The bytes are on the server: keep only the record on disk (drafts and the outbox still reference it).
    void del(`b:${r.id}`, this.store).catch(() => undefined);
    void this.persist(r.id);
  }

  private dispatch(r: Rec, e: Parameters<typeof Files.uploadReducer>[1]): void {
    const next = Files.uploadReducer(r.state, e);
    if (next === r.state) return;
    const phaseChanged = next.phase !== r.state.phase;
    r.state = next;
    if (phaseChanged) void this.persist(r.id);
    this.changed();
  }

  private changed(): void {
    this.version += 1;
    this.snapshots.clear();
    for (const l of Array.from(this.listeners)) l();
  }

  private snapshot(ws: string): UploadEntry[] {
    const cached = this.snapshots.get(ws);
    if (cached !== undefined) return cached;
    const list: UploadEntry[] = [];
    for (const r of this.recs.values()) {
      if (r.ws !== ws) continue;
      const e: UploadEntry = { id: r.id, target: r.target, name: r.name, mime: r.mime, size: r.size, lastModified: r.lastModified, state: r.state, createdAt: r.createdAt };
      if (r.width !== undefined) e.width = r.width;
      if (r.height !== undefined) e.height = r.height;
      if (r.instant === true) e.instant = true;
      const kind = Files.fileKind(r.mime, r.name);
      if ((kind === "image" || kind === "video") && (this.blobs.has(r.id) || this.sentBlobs.has(r.state.file?.id ?? ""))) e.previewUrl = `local-upload:${r.id}`;
      list.push(e);
    }
    list.sort((a, b) => a.createdAt - b.createdAt);
    this.snapshots.set(ws, list);
    return list;
  }

  // ---------- Persistence ----------

  private async persist(id: string): Promise<void> {
    const r = this.recs.get(id);
    if (r === undefined) return;
    const stored: StoredRec = { rec: { ...r, state: r.state.phase === "uploading" ? Files.uploadReducer(r.state, { type: "pause" }) : r.state } };
    const thumb = this.thumbs.get(id);
    if (thumb !== undefined) stored.thumb = thumb;
    try {
      await set(`r:${id}`, stored, this.store);
    } catch {
      // Private mode / quota: uploads still work, they just won't survive a reload.
    }
  }

  private async restore(): Promise<void> {
    let all: [string, unknown][] = [];
    try {
      all = await entries<string, unknown>(this.store);
    } catch {
      return;
    }
    const bytes = new Map<string, Blob>();
    for (const [key, v] of all) if (key.startsWith("b:") && v instanceof Blob) bytes.set(key.slice(2), v);
    const now = Date.now();
    for (const [key, v] of all) {
      if (!key.startsWith("r:")) continue;
      const r = (v as StoredRec).rec;
      const blob = bytes.get(r.id);
      if ((r.state.phase === "done" && now - r.createdAt > DONE_TTL_MS) || (r.state.phase !== "done" && blob === undefined)) {
        void del(key, this.store).catch(() => undefined);
        void del(`b:${r.id}`, this.store).catch(() => undefined);
        continue;
      }
      if (r.state.phase === "uploading") r.state = Files.uploadReducer(r.state, { type: "pause" });
      this.recs.set(r.id, r);
      if (blob !== undefined) this.blobs.set(r.id, blob);
      const thumb = (v as StoredRec).thumb;
      if (thumb !== undefined) this.thumbs.set(r.id, thumb);
    }
    this.changed();
    this.pump();
  }

  /** Bumped on every change (media resolver version). */
  get changes(): number {
    return this.version;
  }
}
